import React, { useCallback, useEffect, useMemo, useState } from "react";
import isNil from "lodash/isNil";
import { useBorrowerSubmissionsHook } from "../../hooks/useBorrowerSubmissionsHook";
import { LINK_KIND_CREDIT_AUTHORIZATION } from "../../hooks/useApplicationLinksHook";
import PublicFormShell from "../../components/publicForm/PublicFormShell";
import PublicFormSuccess from "../../components/publicForm/PublicFormSuccess";
import DraftRestoreBanner from "../../components/publicForm/DraftRestoreBanner";
import usePublicFormDraft, {
  ssnSubmitCheck,
} from "../../components/publicForm/usePublicFormDraft";
import usePublicLinkPrefill from "../../components/publicForm/usePublicLinkPrefill";
import PublicLinkUnavailable from "../../components/publicForm/PublicLinkUnavailable";
import DevPrefillBar from "../../components/publicForm/DevPrefillBar";
import { buildDevCreditAuthValues } from "./devPrefillValues";
import StepCreditAuthDetails from "./steps/StepCreditAuthDetails";
import StepCreditAuthIdentification from "./steps/StepCreditAuthIdentification";
import StepCreditAuthSign from "./steps/StepCreditAuthSign";

/**
 * Public, unauthenticated credit authorization (`/credit-auth`).
 *
 * Mounted OUTSIDE ProtectedRoute — there is no session here, so nothing in this
 * tree may read Redux user state or call an authenticated endpoint. Submits to
 * `POST borrower_submissions/new_credit_authorization`.
 *
 * Query params:
 *   ?hideLogo=true       — render without the partner logo (for embedding)
 *   ?brandingName=<name> — render a custom logo name instead
 *
 * Field names are a wire contract — see creditAuthShared.js.
 */

// Must not collide with the loan application's key — same origin.
const DRAFT_STORAGE_KEY = "brokerPortal.creditAuthorization.draft.v1";

const STEPS = [
  {
    label: "Your Details",
    sublabel: "Name, contact, and identity",
    title: "Let's start with you",
    description:
      "We pull your credit report against these details, so please make sure they match your records.",
    Component: StepCreditAuthDetails,
    submitCheck: ssnSubmitCheck,
  },
  {
    label: "Photo ID",
    sublabel: "Government issued identification",
    title: "Verify your identity",
    description: "Upload a clear copy of your government issued photo ID.",
    Component: StepCreditAuthIdentification,
  },
  {
    label: "Sign & Submit",
    sublabel: "Authorization and signature",
    title: "Authorize and sign",
    description:
      "Read the authorization agreement below, then sign to allow the credit and background checks.",
    Component: StepCreditAuthSign,
  },
];

const CreditAuthorizationFormScreen = ({
  initialValues,
  hideLogo: hideLogoProp = false,
}) => {
  const [success, setSuccess] = useState(false);
  const { loading, createCreditAuthorization } = useBorrowerSubmissionsHook();

  // The prop sets the baseline (the /unbranded route); ?hideLogo=true can turn
  // it on for the plain route but never turns the prop back off.
  const [hideLogo, setHideLogo] = useState(hideLogoProp);
  const [customLogoName, setCustomLogoName] = useState(null);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      setHideLogo(hideLogoProp || params.get("hideLogo") === "true");
      setCustomLogoName(params.get("brandingName") || null);
    } catch (e) {
      setHideLogo(hideLogoProp);
      setCustomLogoName(null);
    }
  }, [hideLogoProp]);

  // Draft persistence is only meaningful for a fresh authorization; when the
  // caller seeds initialValues we're editing something specific, so leave it off.
  const draftEnabled = isNil(initialValues);
  const { restored, checked, save, clear, dismissRestore } = usePublicFormDraft({
    storageKey: DRAFT_STORAGE_KEY,
    enabled: draftEnabled,
  });
  const [draftChoice, setDraftChoice] = useState(null);

  // Local-dev prefill. Held as the values themselves rather than a flag so the
  // File and the drawn signature are built once, at click time — rebuilding them
  // on every render would hand react-final-form a new `initialValues` identity
  // each pass.
  const [devValues, setDevValues] = useState(null);

  // `?t=<token>` (or a migrated `?link_id=`) — resolved server-side into prefill.
  const {
    prefill,
    checked: linkChecked,
    linkStatus,
    token,
    linkId,
    reportStarted,
    hasUrlParams,
  } = usePublicLinkPrefill(LINK_KIND_CREDIT_AUTHORIZATION);

  // A restored draft is the borrower's own work and so outranks the prefill.
  const init = useMemo(() => {
    // Dev prefill outranks everything — it exists precisely to blow past
    // whatever state the form would otherwise open in.
    if (!isNil(devValues)) return devValues;
    if (!isNil(initialValues)) return initialValues;
    const base = { ...(prefill || {}) };
    if (draftChoice === "restored" && restored?.values) {
      return { ...base, ...restored.values };
    }
    return base;
  }, [initialValues, draftChoice, restored, prefill, devValues]);

  const onFormSubmit = (data) => {
    createCreditAuthorization({
      payload: data,
      // Identifies the link this submission came through — the server attributes
      // it to the sending LO and closes the funnel on it.
      token,
      linkId,
      onSuccessFn: () => {
        clear();
        setSuccess(true);
      },
    });
  };

  const handleValuesChange = useCallback(
    (values, step) => {
      reportStarted();
      if (draftEnabled) save(values, step);
    },
    [save, draftEnabled, reportStarted],
  );

  if (success) {
    return (
      <PublicFormSuccess
        hideLogo={hideLogo}
        customLogoName={customLogoName}
        title="Authorization received"
        message="Thank you — your credit authorization has been submitted to our team."
        nextSteps={[
          "Our team reviews your authorization and the ID you uploaded.",
          "We'll run the credit and background checks you authorized.",
          "We'll reach out at the email address you provided with next steps.",
        ]}
      />
    );
  }

  // A link that resolved to anything but "ok" must not show the form: the
  // submit would 410 after the borrower had signed it. `null` is the no-link
  // case (a direct visit), which still gets a blank form.
  if (linkChecked && linkStatus && linkStatus !== "ok") {
    return <PublicLinkUnavailable status={linkStatus} hideLogo={hideLogo} />;
  }

  // Hold the form back until the draft and link lookups resolve — mounting with
  // empty values and then swapping initialValues underneath would reset the form.
  if (!linkChecked) return null;
  if (draftEnabled && !checked) return null;

  // Only ever offered on a plain visit. A URL carrying params arrived from
  // somewhere — a sent link, an embed with context — and its prefill is the
  // answer the borrower should be looking at, not a stale draft from a previous
  // visit. Asking them to choose between the two invites picking the wrong one.
  // The draft is still saved either way; a later plain visit can still restore it.
  const showDraftBanner =
    Boolean(restored) && isNil(draftChoice) && !hasUrlParams;

  return (
    <PublicFormShell
      // Remount on restore (and on a dev prefill) so react-final-form picks up
      // the new initialValues — it reads them once, at mount.
      key={devValues ? "dev-prefill" : draftChoice || "fresh"}
      steps={STEPS}
      onSubmit={onFormSubmit}
      initialValues={init}
      initialStep={draftChoice === "restored" ? (restored?.step ?? 0) : 0}
      loading={loading}
      hideLogo={hideLogo}
      customLogoName={customLogoName}
      formTitle="Credit Authorization"
      submitLabel="Submit Authorization"
      onValuesChange={handleValuesChange}
      devTools={
        <DevPrefillBar
          onPrefill={() => setDevValues(buildDevCreditAuthValues())}
          note="Local only — fills all three steps, including the photo ID and signature, so you can click straight to submit."
        />
      }
      draftBanner={
        showDraftBanner ? (
          <DraftRestoreBanner
            savedAt={restored?.savedAt}
            onRestore={() => setDraftChoice("restored")}
            onDiscard={() => {
              clear();
              setDraftChoice("discarded");
            }}
            // Keeps the saved answers, just stops asking — here and on every
            // future visit from this browser.
            onIgnore={() => {
              dismissRestore();
              setDraftChoice("ignored");
            }}
          />
        ) : null
      }
    />
  );
};

export default CreditAuthorizationFormScreen;
